import { useState, useRef } from 'react'
import { Search, Check, ChevronDown, ChevronUp, X, Plus } from 'lucide-react'
import { useI18n } from '../i18n'

/**
 * SearchableChecklist — shows common items by default, full list via search
 *
 * Default view: selected items (top) + common items (below) + search field + "Add other"
 * Search view: filtered results from the FULL list + "Add other"
 *
 * Props:
 *   items           — array of { id, name, category?, common? }
 *   selectedItems   — array of selected items (with extra fields)
 *   onToggle        — (item) => void
 *   onRemoveCustom  — (id) => void
 *   onAddCustom     — () => void — opens "add custom" modal
 *   addLabel        — string
 *   renderExpanded  — (item) => JSX
 *   expandedId      — string|null
 *   onExpand        — (id|null) => void
 *   selectedColor   — 'blue' | 'amber'
 *   placeholder     — search placeholder
 */
export default function SearchableChecklist({
  items = [],
  selectedItems = [],
  onToggle,
  onRemoveCustom,
  onAddCustom,
  addLabel = 'Add other',
  renderExpanded,
  expandedId,
  onExpand,
  selectedColor = 'blue',
  placeholder = 'Search...',
}) {
  const { t } = useI18n()
  const [query, setQuery] = useState('')
  const inputRef = useRef(null)
  const selectedIds = new Set(selectedItems.map(s => s.id))
  const isSearching = query.trim().length > 0
  const normalizedQuery = query.toLowerCase().trim()

  // Color schemes
  const colors = selectedColor === 'amber'
    ? { border: 'border-amber-300', bg: 'bg-amber-50/50', checkBorder: 'border-amber-500', checkBg: 'bg-amber-500', textCls: 'text-amber-800', badge: { bg: '#FEF3C7', color: '#D97706' } }
    : { border: 'border-[#46A8F9]', bg: 'bg-[#E9F8FF]', checkBorder: 'border-[#46A8F9]', checkBg: 'bg-[#46A8F9]', textCls: '', badge: { bg: '#E9F8FF', color: '#46A8F9' } }

  // What to show
  const commonItems = items.filter(i => i.common && !selectedIds.has(i.id))
  const searchResults = isSearching
    ? items.filter(i => !selectedIds.has(i.id) && (i.name.toLowerCase().includes(normalizedQuery) || (i.category && i.category.toLowerCase().includes(normalizedQuery))))
    : []

  // Custom items not in master list
  const customSelected = selectedItems.filter(s => s.custom)
  // Selected items that are in master list
  const standardSelected = selectedItems.filter(s => !s.custom)

  const renderItem = (item) => {
    const isSelected = selectedIds.has(item.id)
    const selectedItem = isSelected ? selectedItems.find(s => s.id === item.id) : null
    const isExpanded_ = expandedId === item.id

    return (
      <div key={item.id} className={`rounded-[14px] border-[1.5px] overflow-hidden transition-all ${isSelected ? `${colors.border} ${colors.bg}` : 'border-gray-200'}`}>
        <button onClick={() => onToggle(item)} className="w-full px-3.5 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 ${isSelected ? `${colors.checkBorder} ${colors.checkBg}` : 'border-gray-300'}`}>
              {isSelected && <Check size={12} className="text-white" />}
            </div>
            <span className={`text-[13px] ${isSelected ? `font-semibold ${colors.textCls}` : 'font-medium text-gray-600'}`} style={isSelected && selectedColor !== 'amber' ? { color: '#15354F' } : {}}>
              {item.name}
            </span>
          </div>
          {isSelected && renderExpanded && onExpand && (
            <div className="flex items-center gap-2" onClick={(e) => { e.stopPropagation(); onExpand(isExpanded_ ? null : item.id) }}>
              {selectedItem?.severity && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: colors.badge.bg, color: colors.badge.color }}>{selectedItem.severity}</span>}
              {selectedItem?.year && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: colors.badge.bg, color: colors.badge.color }}>{selectedItem.year}</span>}
              {isExpanded_ ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </div>
          )}
        </button>
        {isSelected && isExpanded_ && renderExpanded && (
          <div className={`px-3.5 pb-3 border-t ${selectedColor === 'amber' ? 'border-amber-200' : 'border-blue-100'}`}>
            {renderExpanded(selectedItem || item)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Search field — always at top */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors"
          style={{ color: '#15354F' }}
        />
        {query && <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2"><X size={16} className="text-gray-400" /></button>}
      </div>

      {/* Search results — only when searching */}
      {isSearching && (
        <div className="space-y-1.5 mb-2">
          {searchResults.length > 0 ? (
            searchResults.map(item => renderItem(item))
          ) : (
            <p className="text-[12px] text-gray-400 text-center py-3">No matches for \u201C{query}\u201D — try a different term or add it below.</p>
          )}
          <p className="text-[10px] text-gray-300 text-center">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</p>
        </div>
      )}

      {/* Selected items */}
      {(standardSelected.length > 0 || customSelected.length > 0) && (
        <div className="space-y-1.5 mb-3">
          {standardSelected.map(item => renderItem(item))}
          {customSelected.map(item => (
            <div key={item.id} className={`rounded-[14px] border-[1.5px] ${colors.border} ${colors.bg} p-3 flex items-center justify-between`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Check size={14} className={selectedColor === 'amber' ? 'text-amber-500' : 'text-[#46A8F9]'} />
                <span className="text-[13px] font-semibold truncate" style={{ color: '#15354F' }}>{item.name}</span>
                {item.severity && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: colors.badge.bg, color: colors.badge.color }}>{item.severity}</span>}
                {item.year && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: colors.badge.bg, color: colors.badge.color }}>{item.year}</span>}
              </div>
              {onRemoveCustom && <button onClick={() => onRemoveCustom(item.id)} className="ml-2 flex-shrink-0"><X size={16} className="text-red-400" /></button>}
            </div>
          ))}
        </div>
      )}

      {/* Common items — only when NOT searching */}
      {!isSearching && commonItems.length > 0 && (
        <div className="space-y-1.5 mb-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300 px-1 mb-1">{t('common.common')}</p>
          {commonItems.map(item => renderItem(item))}
        </div>
      )}

      {/* Add custom */}
      {onAddCustom && (
        <button onClick={onAddCustom}
          className="w-full py-2.5 mt-1 border-[1.5px] border-dashed rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2"
          style={{ borderColor: 'rgba(70,168,249,0.3)', color: '#46A8F9' }}>
          <Plus size={16} /> {addLabel}
        </button>
      )}
    </div>
  )
}
